import { Fragment, useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import { Menu } from "lucide-react";
import { useTranslation } from "react-i18next";

import PortalSidebar from "../PortalSidebar/PortalSidebar";
import Footer from "../Footer/Footer";
import CookieBanner from "../CookieBanner/CookieBanner";
import CookieSettings from "../CookieSettings/CookieSettings";
import shellStyles from "./RootLayout.module.css";

const DESKTOP_MQ = "(min-width: 1024px)";
const SIDEBAR_PREF_KEY = "portal.sidebar.desktopVisible";

function readDesktopSidebarPref() {
  try {
    const v = localStorage.getItem(SIDEBAR_PREF_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch {}
  return true;
}

export default function RootLayout() {
  usePageTitle();
  const { t } = useTranslation("common");

  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DESKTOP_MQ).matches
      : true,
  );
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(
    readDesktopSidebarPref,
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const apply = () => {
      setIsDesktop(mq.matches);
      if (mq.matches) setSidebarOpen(false);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_PREF_KEY, desktopSidebarVisible ? "1" : "0");
    } catch {}
  }, [desktopSidebarVisible]);

  const handleOpenCookieSettings = () => {
    setShowCookieBanner(true);
  };

  const handleCloseCookieBanner = () => {
    setShowCookieBanner(false);
  };

  const overlayActive = !isDesktop && sidebarOpen;
  const shellInset = isDesktop && desktopSidebarVisible;
  const showMenuBar = !isDesktop || !desktopSidebarVisible;

  const handleMenuButtonClick = () => {
    if (isDesktop) {
      setDesktopSidebarVisible(true);
    } else {
      setSidebarOpen(true);
    }
  };

  const menuExpanded = !isDesktop ? sidebarOpen : desktopSidebarVisible;

  return (
    <Fragment>
      {overlayActive && (
        <button
          type="button"
          className={shellStyles.overlayVisible}
          aria-label={t("sidebar.closeMenu")}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div className={shellStyles.shell}>
        <PortalSidebar
          open={sidebarOpen}
          isDesktop={isDesktop}
          desktopVisible={desktopSidebarVisible}
          onNavigate={() => setSidebarOpen(false)}
          onCollapseDesktop={() => setDesktopSidebarVisible(false)}
        />
        <div
          className={`${shellStyles.shellInner} ${
            shellInset ? "" : shellStyles.shellInnerNoInset
          }`}
        >
          <div className={shellStyles.mainColumn}>
            {showMenuBar ? (
              <button
                type="button"
                className={shellStyles.burgerBtn}
                aria-label={
                  isDesktop ? t("sidebar.expandSidebar") : t("sidebar.openMenu")
                }
                aria-expanded={menuExpanded}
                onClick={handleMenuButtonClick}
              >
                <Menu size={22} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
            <Outlet />
            <Footer variant="portal" />
          </div>
        </div>
      </div>

      <CookieBanner />

      {showCookieBanner && (
        <CookieBanner forceShow={true} onClose={handleCloseCookieBanner} />
      )}

      <CookieSettings onOpenSettings={handleOpenCookieSettings} />
    </Fragment>
  );
}
